import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SimpleContact } from '@texas/api/endpoints/contactsApi';
import { Icons } from '../Icons';
import {
  ApprovalDraft,
  ApprovalValues,
  getApprovalIssues,
} from './bulkApprovalState';

interface RowProps {
  draft: ApprovalDraft;
  onContact: (field: 'supplierContact' | 'customerContact') => void;
  onDate: (date: string) => void;
  onReset: (field: keyof ApprovalValues) => void;
  onToggle: () => void;
}

interface ContactButtonProps {
  contact: SimpleContact | null;
  placeholder: string;
  label: string;
  onClick: () => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
}

export function BulkApprovalRow({
  draft,
  onContact,
  onDate,
  onReset,
  onToggle,
}: RowProps) {
  const { t } = useTranslation();
  const { sample, ignored, overrides } = draft;
  const issues = getApprovalIssues(draft);
  const label = [
    sample.articleItemCode,
    sample.matrixNodeXValue,
    sample.matrixNodeYValue,
    sample.branchName,
  ]
    .filter(Boolean)
    .join(' · ');
  const contactFields = ['supplierContact', 'customerContact'] as const;

  return (
    <Flex
      id={`bulk-pa-${sample.compositionGroupId}`}
      px={4}
      py="14px"
      gap={4}
      align="center"
      minH="76px"
      borderTopWidth="1px"
      borderColor="entityDirectory.border"
      bg={ignored ? 'entityDirectory.raisedSurface' : 'entityDirectory.surface'}
    >
      <Box w="180px" flexShrink={0} opacity={ignored ? 0.6 : 1}>
        <Text fontSize="sm" fontWeight="semibold">
          {[sample.matrixNodeXValue, sample.matrixNodeYValue]
            .filter(Boolean)
            .join(' · ') || sample.articleItemCode}
        </Text>
        <Text fontSize="xs" color="entityDirectory.mutedText">
          {sample.articleItemCode} · {sample.articleName}
        </Text>
        <Text fontSize="xs" color="entityDirectory.mutedText">
          {t('compositionGroup.option.optionNr', { nr: sample.option })} ·{' '}
          {t('compositionGroup.version.versionNr', { nr: sample.version })}
        </Text>
      </Box>
      <Box w="130px" flexShrink={0} opacity={ignored ? 0.6 : 1}>
        <Text fontSize="sm">{sample.branchName}</Text>
        <Text fontSize="xs" color="entityDirectory.mutedText">
          {sample.supplierName}
        </Text>
      </Box>
      {contactFields.map((field) => (
        <FormControl
          key={field}
          flex={1}
          minW={0}
          isInvalid={issues.includes(field)}
          isDisabled={ignored}
        >
          <ApprovalContactButton
            contact={draft[field]}
            placeholder={t(
              field === 'supplierContact'
                ? 'productApproval.selectSupplierContact'
                : 'productApproval.selectBrandContact',
            )}
            label={t('productApproval.bulk.contactFor', {
              contact: t(
                field === 'supplierContact'
                  ? 'suppliers.supplierContact'
                  : 'customer.customerContact',
              ),
              approval: label,
            })}
            isInvalid={issues.includes(field)}
            isDisabled={ignored}
            onClick={() => onContact(field)}
          />
          <FormErrorMessage fontSize="xs">
            {t(
              field === 'supplierContact'
                ? 'productApproval.supplierContactRequired'
                : 'productApproval.brandContactRequired',
            )}
          </FormErrorMessage>
          {overrides[field] && !ignored && (
            <ResetField onClick={() => onReset(field)} />
          )}
        </FormControl>
      ))}
      <FormControl
        w="160px"
        flexShrink={0}
        isInvalid={issues.includes('sampleReadyDate')}
        isDisabled={ignored}
      >
        <Input
          type="date"
          variant="outline"
          size="sm"
          h={9}
          borderRadius="4px"
          bg="entityDirectory.inputBackground"
          borderColor="entityDirectory.border"
          value={draft.sampleReadyDate}
          aria-label={t('productApproval.bulk.dateFor', { approval: label })}
          onChange={(event) => onDate(event.target.value)}
        />
        <FormErrorMessage fontSize="xs">
          {t('productApproval.bulk.dateRequired')}
        </FormErrorMessage>
        {overrides.sampleReadyDate && !ignored && (
          <ResetField onClick={() => onReset('sampleReadyDate')} />
        )}
      </FormControl>
      <Flex w="110px" flexShrink={0} align="center" justify="flex-end" gap={2}>
        <Text
          fontSize="xs"
          _dark={{
            color: ignored
              ? 'entityDirectory.mutedText'
              : issues.length
              ? 'red.200'
              : 'green.200',
          }}
          color={
            ignored
              ? 'entityDirectory.mutedText'
              : issues.length
              ? 'entityDirectory.dangerText'
              : 'entityDirectory.successText'
          }
        >
          {t(
            ignored
              ? 'productApproval.bulk.excluded'
              : issues.length
              ? 'productApproval.bulk.needsInputLabel'
              : 'productApproval.bulk.ready',
          )}
        </Text>
        <Tooltip
          label={t(
            ignored
              ? 'productApproval.bulk.restore'
              : 'productApproval.bulk.exclude',
          )}
        >
          <IconButton
            aria-label={t(
              ignored
                ? 'productApproval.bulk.restoreApproval'
                : 'productApproval.bulk.excludeApproval',
              { approval: label },
            )}
            icon={ignored ? <Icons.Undo /> : <Icons.TrashOutline />}
            variant="ghost"
            size="sm"
            onClick={onToggle}
          />
        </Tooltip>
      </Flex>
    </Flex>
  );
}

export function ApprovalContactButton({
  contact,
  placeholder,
  label,
  onClick,
  isInvalid = false,
  isDisabled = false,
}: ContactButtonProps) {
  return (
    <Button
      variant="outline"
      w="full"
      h={10}
      px={3}
      justifyContent="space-between"
      fontSize="sm"
      fontWeight="normal"
      borderRadius="4px"
      bg="entityDirectory.inputBackground"
      borderColor={isInvalid ? 'red.400' : 'entityDirectory.border'}
      color={isInvalid ? 'entityDirectory.dangerText' : 'entityDirectory.text'}
      _dark={isInvalid ? { color: 'red.200' } : undefined}
      rightIcon={<Icons.ChevronDown />}
      onClick={onClick}
      aria-label={label}
      aria-invalid={isInvalid}
      isDisabled={isDisabled}
    >
      <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {contact?.name ?? placeholder}
      </Text>
    </Button>
  );
}

function ResetField({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Flex mt={1} gap={1} align="center" color="entityDirectory.mutedText">
      <Text fontSize="xs">{t('productApproval.bulk.changed')}</Text>
      <Button variant="link" size="xs" onClick={onClick}>
        {t('productApproval.bulk.reset')}
      </Button>
    </Flex>
  );
}
